import SaveIcon from "@mui/icons-material/Save";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useState } from "react";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import CreatorCrossService from "../../../creator/service/CreatorCrossService";
import Logger from "../../../services/Logger";
import AdminCrossBoardService from "../../pzl_cross/board/AdminCrossBoardService";
import CustomTags from "../../components/CustomTags";

function normalizeTagsFromPuzzle(puzzle) {
    const raw = puzzle?.tags;
    if (!Array.isArray(raw)) return [];
    return raw
        .map((t) => (typeof t === "string" ? t : t?.tag ?? String(t)))
        .map((s) => String(s).trim())
        .filter(Boolean);
}

/**
 * Tags editor with autocomplete (published crossword tag catalog) for admin published crossword step.
 */
export default function PublishedCrossTagsEditor({ puzzle, onSaved, onError }) {
    const { language } = useAdminLanguage();
    const [tags, setTags] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [flash, setFlash] = useState(null);

    const adminLang = language || "english";

    useEffect(() => {
        setTags(normalizeTagsFromPuzzle(puzzle));
    }, [puzzle?.id, puzzle?.tags]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setTagsLoading(true);
            try {
                const list = await CreatorCrossService.getCrossTags(adminLang, "PUBLISHED");
                if (!cancelled) setTagOptions(Array.isArray(list) ? list : []);
            } catch (e) {
                Logger.error("PublishedCrossTagsEditor: load tag options", e);
                if (!cancelled) setTagOptions([]);
            } finally {
                if (!cancelled) setTagsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [adminLang]);

    const dirty = useMemo(() => {
        const a = [...normalizeTagsFromPuzzle(puzzle)].map((t) => t.toLowerCase()).sort().join("|");
        const b = [...tags].map((t) => String(t).trim().toLowerCase()).sort().join("|");
        return a !== b;
    }, [puzzle, tags]);

    const handleSave = async () => {
        if (!puzzle?.id) {
            onError?.("Missing puzzle id.");
            return;
        }
        const title = typeof puzzle.title === "string" ? puzzle.title.trim() : "";
        if (!title) {
            onError?.("Title is required to save; fix it in Puzzle Details.");
            return;
        }

        setSaving(true);
        setFlash(null);
        onError?.(null);
        try {
            const formattedTags = tags.map((t) => String(t).trim()).filter((t) => t.length > 0);
            await AdminCrossBoardService.updatePuzzleInfo(
                puzzle.id,
                title,
                Boolean(puzzle.free ?? puzzle.isFree),
                formattedTags
            );
            setFlash({ severity: "success", message: "Tags saved." });
            onSaved?.();
        } catch (e) {
            Logger.error("PublishedCrossTagsEditor: save", e);
            const msg = e?.message || "Failed to save tags.";
            setFlash({ severity: "error", message: msg });
            onError?.(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#3BA666", mb: 1 }}>
                Tags
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Pick from published crossword tags or type a new tag (Enter). Save to apply.
            </Typography>
            <CustomTags
                value={tags}
                onChange={(e, newTags) => setTags(newTags)}
                options={tagOptions}
                loading={tagsLoading}
                label="Tags"
                placeholder="Search or add tags…"
                helperText=""
                sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    disabled={saving || !dirty}
                    onClick={handleSave}
                >
                    Save tags
                </Button>
            </Box>
            {flash ? (
                <Alert severity={flash.severity} sx={{ mt: 1.5 }} onClose={() => setFlash(null)}>
                    {flash.message}
                </Alert>
            ) : null}
        </Box>
    );
}
